import * as React from 'react';
import { useWorkspace } from './WorkspaceContext';
import { useNotifications, type UseNotificationsResult } from '../hooks/useNotifications';

/**
 * One shared notifications source for the whole app - the header bell and the floating job toaster
 * both read it, so they stay in sync and there is a single poll. Enabled only once a workspace is
 * active (the server scopes by the user's membership).
 */
export interface NotificationsContextValue extends UseNotificationsResult {
  enabled: boolean;
}

const NotificationsContext = React.createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { workspace } = useWorkspace();
  const enabled = !!workspace;
  const value = useNotifications(enabled, workspace?.uuid);
  return (
    <NotificationsContext.Provider value={{ ...value, enabled }}>{children}</NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = React.useContext(NotificationsContext);
  if (!ctx) {
    // Safe no-op default (e.g. rendered outside the provider) - never throws into the UI.
    return {
      enabled: false,
      notifications: [],
      unreadCount: 0,
      loading: false,
      refresh: () => {},
      markRead: () => {},
      markAllRead: () => {},
    };
  }
  return ctx;
}
